import { removeTrailingSlash, ISODateStringToTimestamp } from "@sky-it-livedata-libraries/livedata-commons";
import { MotorsportsSessionRowSession } from '@sky-it-livedata/livedata-sports-design-system';

import {
  FREE_PRACTICE_ONE,
  FREE_PRACTICE_TWO,
  FREE_PRACTICE_THREE,
  FREE_PRACTICE_FOUR,
  QUALIFYING_GROUP,
  QUALIFYING_ONE,
  QUALIFYING_TWO,
  QUALIFYING_THREE,
  RACE,
  RACE_SPRINT,
  RACE_STATUS_FIXTURE,
  RACE_STATUS_PLAYED,
  RACE_STATUS_PLAYING,
  RACE_STATUS_CANCELLED,
  RACE_STATUS_POSTPONED,
  RACE_STATUS_SUSPENDED,
  RaceSessionType,
  SHOOTOUT_SPRINT_GROUP,
  SHOOTOUT_SPRINT_ONE,
  SHOOTOUT_SPRINT_THREE,
  SHOOTOUT_SPRINT_TWO,
  STARTING_GRID,
  STARTING_GRID_SPRINT,
  WARMUP,
} from "../types/race-session";

export const getSessionUrl = (
  baseUrl: string,
  sessionName: RaceSessionType
): string => `${removeTrailingSlash(baseUrl)}${sessionUrlMap[sessionName]}`;

export const sessionUrlMap = {
  [FREE_PRACTICE_ONE]: "/libere",
  [FREE_PRACTICE_TWO]: "/libere?fase=2",
  [FREE_PRACTICE_THREE]: "/libere?fase=3",
  [FREE_PRACTICE_FOUR]: "/libere?fase=4",
  [QUALIFYING_GROUP]: "/qualifiche",
  [QUALIFYING_ONE]: "/qualifiche",
  [QUALIFYING_TWO]: "/qualifiche",
  [QUALIFYING_THREE]: "/qualifiche",
  [SHOOTOUT_SPRINT_ONE]: "/sprint-shootout",
  [SHOOTOUT_SPRINT_TWO]: "/sprint-shootout",
  [SHOOTOUT_SPRINT_THREE]: "/sprint-shootout",
  [STARTING_GRID]: "/qualifiche/griglia",
  [RACE_SPRINT]: "/sprint-race",
  [RACE]: "",
};

export const BLACK_LIST_SESSIONS = [STARTING_GRID, STARTING_GRID_SPRINT, WARMUP];
export const Q_SESSIONS = [QUALIFYING_ONE, QUALIFYING_TWO, QUALIFYING_THREE];
export const SS_SESSIONS = [SHOOTOUT_SPRINT_ONE, SHOOTOUT_SPRINT_TWO, SHOOTOUT_SPRINT_THREE];

/** Filter utils  */
export const filterSessions = (session: MotorsportsSessionRowSession): boolean =>
    !BLACK_LIST_SESSIONS.includes(session.sessionName);
  export const filterQualifyingSessions = (session: MotorsportsSessionRowSession): boolean =>
    !Q_SESSIONS.includes(session.sessionName);
  export const filterShootoutSessions = (session: MotorsportsSessionRowSession): boolean =>
    !SS_SESSIONS.includes(session.sessionName);

/**
 * Build the GP sessions
 * @param gpSessions transformed sessions
 * @param filterQualifingAndShootout if set to true, filters qualifing and shootout sessions
 * @returns the correct gp sessions to show
 */
export const buildGPSessionsByDate = <T extends MotorsportsSessionRowSession>(
  gpSessions: Array<T>,
  filterQualifingAndShootout: boolean = true
): Array<T> => {
  /**
   * DYNAMIC code - Get ALL SESSIONS excluding
   * - QUALIFYING (to be grouped using findGroupedSessions)
   * - SHOOTOUT (to be grouped using findShootoutSession)
   * - GRID (not a real session)
   */
  const sessions: ReadonlyArray<T> = filterQualifingAndShootout
    ? gpSessions
        .filter(filterQualifyingSessions)
        .filter(filterShootoutSessions)
        .filter(filterSessions)
    : gpSessions.filter(filterSessions);

  // 1st SORT: do NOT trust FPx, Qx and SSx SESSIONS to be in the RIGHT ORDER!
  const sortedGSessions = [...gpSessions].sort(
    (prev: T, next: T) =>
      (ISODateStringToTimestamp(prev.sessionDate) ?? 0) -
      (ISODateStringToTimestamp(next.sessionDate) ?? 0)
  );
  const qSession = findGroupedSessions(
    sortedGSessions,
    Q_SESSIONS,
    QUALIFYING_GROUP
  );
  const sSession = findGroupedSessions(
    sortedGSessions,
    SS_SESSIONS,
    SHOOTOUT_SPRINT_GROUP
  );
  const sessionArray = filterQualifingAndShootout
    ? [...sessions, qSession, sSession]
    : sessions;

  return (
    (sessionArray
      .filter((elm) => Boolean(elm))
      // 2nd SORT: due to [...sessions, qSession, sSession] array to be NOT in the right DATE/TIME order!
      .sort(
        (prev: T, next: T) =>
          (ISODateStringToTimestamp(prev.sessionDate) ?? 0) -
          (ISODateStringToTimestamp(next.sessionDate) ?? 0)
      ) ?? []) as Array<T>
  );
};

export const findGroupedSessions = (
    gpSessions: ReadonlyArray<MotorsportsSessionRowSession>,
    sessionsType: ReadonlyArray<RaceSessionType>,
    sessionsGroup: RaceSessionType
  ): MotorsportsSessionRowSession => {
    const sessions: ReadonlyArray<MotorsportsSessionRowSession> = gpSessions
      .filter((session) => sessionsType.includes(session.sessionName))
      .map((session) => {
        return { ...session, sessionName: sessionsGroup }; // flat all sessions, so [Q1,Q2,Q3] -> [Q,Q,Q]
      });
  
    const isAllFixture: boolean = sessions.every(
      (session: MotorsportsSessionRowSession) => session.sessionStatus === RACE_STATUS_FIXTURE
    );
    const isAllPlayed: boolean = sessions.every(
      (session: MotorsportsSessionRowSession) => session.sessionStatus === RACE_STATUS_PLAYED
    );
    const playingSession: MotorsportsSessionRowSession | undefined = sessions.find(
      (session) => session.sessionStatus === RACE_STATUS_PLAYING
    );
  
    switch (true) {
      case isAllFixture:
        return sessions.at(0)!;
      case isAllPlayed:
        return sessions.at(-1)!;
      case Boolean(playingSession):
        return playingSession!;
      default:
        return (
          sessions.find(
            (session) =>
              session.sessionStatus === RACE_STATUS_CANCELLED ||
              session.sessionStatus === RACE_STATUS_POSTPONED ||
              session.sessionStatus === RACE_STATUS_SUSPENDED
          ) || sessions.at(0)!
        );
    }
  };
